import React from "react";
import { BrowserRouter, Route } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import Homepage from "./Pages/HomePage";
import CoinPage from "./Pages/CoinPage";
import ModernHeader from "./components/Header/ModernHeader";
import Alert from "./components/Alert";
import WhitePaper from "./Pages/WhitePaper";
import WalletContextProvider from "./WalletContextProvider";
import CryptoContext from "./CryptoContext";
import { colors, typography, borderRadius, shadows } from "./styles/designSystem";
import "./App.css";

const useStyles = makeStyles(() => ({
  App: {
    background: colors.gradients.background,
    color: colors.text.primary,
    minHeight: "100vh",
    fontFamily: typography.fontFamily.primary,
  },
}));

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
      contrastText: colors.primary.contrast,
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
      dark: colors.secondary.dark,
      contrastText: colors.secondary.contrast,
    },
    success: {
      main: colors.success.main,
      light: colors.success.light,
      dark: colors.success.dark,
      contrastText: colors.success.contrast,
    },
    warning: {
      main: colors.warning.main,
      light: colors.warning.light,
      dark: colors.warning.dark,
      contrastText: colors.warning.contrast,
    },
    error: {
      main: colors.error.main,
      light: colors.error.light,
      dark: colors.error.dark,
      contrastText: colors.error.contrast,
    },
    background: {
      default: colors.background.primary,
      paper: colors.background.card,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
  },
  typography: {
    fontFamily: typography.fontFamily.primary,
    h1: {
      fontFamily: typography.fontFamily.primary,
      fontWeight: typography.fontWeight.extraBold,
    },
    h2: {
      fontFamily: typography.fontFamily.primary,
      fontWeight: typography.fontWeight.bold,
    },
    h3: {
      fontFamily: typography.fontFamily.primary,
      fontWeight: typography.fontWeight.bold,
    },
    h4: {
      fontFamily: typography.fontFamily.primary,
      fontWeight: typography.fontWeight.semiBold,
    },
    h5: {
      fontFamily: typography.fontFamily.primary,
      fontWeight: typography.fontWeight.semiBold,
    },
    h6: {
      fontFamily: typography.fontFamily.primary,
      fontWeight: typography.fontWeight.medium,
    },
    body1: {
      fontFamily: typography.fontFamily.secondary,
      fontWeight: typography.fontWeight.regular,
    },
    body2: {
      fontFamily: typography.fontFamily.secondary,
      fontWeight: typography.fontWeight.regular,
    },
  },
  shape: {
    borderRadius: parseInt(borderRadius.lg),
  },
  shadows: [
    "none",
    shadows.sm,
    shadows.base,
    shadows.md,
    shadows.lg,
    shadows.xl,
    shadows['2xl'],
    ...Array(18).fill(shadows['2xl']),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: borderRadius.lg,
          fontFamily: typography.fontFamily.primary,
          fontWeight: typography.fontWeight.semiBold,
          transition: 'all 0.3s ease-in-out',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.xl,
          background: colors.background.card,
          border: `1px solid ${colors.border.neutral}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: colors.background.card,
          border: `1px solid ${colors.border.neutral}`,
        },
      },
    },
  },
});

function App() {
  const classes = useStyles();

  // TODO: Development Mode - Replace placeholder components with actual contract integration
  // This build contains mock data and placeholder functionality for demonstration purposes
  // Real contract integration will be implemented when smart contracts are deployed

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <CryptoContext>
          <WalletContextProvider>
            <div className={classes.App}>
              <ModernHeader />
              <Route path="/" component={Homepage} exact />
              <Route path="/whitepaper" component={WhitePaper} exact />
              <Route path="/coins/:id" component={CoinPage} exact />
            </div>
            <Alert />
          </WalletContextProvider>
        </CryptoContext>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
